import type { InputBinding } from "./input-binding";
import type { InputService } from "./input-service";
import type { InputPoint } from "./types";
import { isInteractiveControlTarget } from "./interactive-target";

function localPoint(surface: HTMLElement, clientX: number, clientY: number): InputPoint {
  const rect = typeof surface.getBoundingClientRect === "function"
    ? surface.getBoundingClientRect()
    : ({ left: 0, top: 0 } as DOMRect);
  return { x: clientX - rect.left, y: clientY - rect.top };
}

function forEachChangedTouch(event: TouchEvent, visit: (touch: Touch) => void): void {
  const touches = event.changedTouches as unknown as ArrayLike<Touch>;
  for (let index = 0; index < touches.length; index += 1) {
    const touch = touches[index];
    if (touch) visit(touch);
  }
}

export function bindTouchFallback(
  surface: HTMLElement,
  scope: Window,
  document: Document,
  input: InputService,
): InputBinding {
  const onTouchStart = (event: TouchEvent): void => {
    if (isInteractiveControlTarget(event.target)) return;
    event.preventDefault();
    forEachChangedTouch(event, (touch) => {
      input.press(
        touch.identifier,
        localPoint(surface, touch.clientX, touch.clientY),
      );
    });
  };
  const onTouchMove = (event: TouchEvent): void => {
    if (isInteractiveControlTarget(event.target)) return;
    event.preventDefault();
    forEachChangedTouch(event, (touch) => {
      input.move(
        touch.identifier,
        localPoint(surface, touch.clientX, touch.clientY),
      );
    });
  };
  const onTouchEnd = (event: TouchEvent): void => {
    if (isInteractiveControlTarget(event.target)) return;
    event.preventDefault();
    forEachChangedTouch(event, (touch) => input.release(touch.identifier));
  };
  const onTouchCancel = (event: TouchEvent): void => {
    if (isInteractiveControlTarget(event.target)) return;
    event.preventDefault();
    forEachChangedTouch(event, (touch) => input.cancel(touch.identifier));
  };
  const releaseAll = (): void => input.releaseAll();
  const onVisibilityChange = (): void => {
    if (document.visibilityState === "hidden") input.releaseAll();
  };

  surface.addEventListener("touchstart", onTouchStart, { passive: false });
  surface.addEventListener("touchmove", onTouchMove, { passive: false });
  surface.addEventListener("touchend", onTouchEnd, { passive: false });
  surface.addEventListener("touchcancel", onTouchCancel, { passive: false });
  scope.addEventListener("blur", releaseAll);
  document.addEventListener("visibilitychange", onVisibilityChange);

  return {
    destroy(): void {
      surface.removeEventListener("touchstart", onTouchStart);
      surface.removeEventListener("touchmove", onTouchMove);
      surface.removeEventListener("touchend", onTouchEnd);
      surface.removeEventListener("touchcancel", onTouchCancel);
      scope.removeEventListener("blur", releaseAll);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      input.releaseAll();
    },
  };
}
